import { Injectable, NgModule } from '@angular/core';
import {
	provideTransloco,
	Translation,
	TranslocoLoader,
	TranslocoModule,
} from '@jsverse/transloco';

import { ReachTranslateLang } from '~app-client/core/types';
import { environment } from '~app-environment';

declare const BUILD_TIME: string;

function isPlainObject(value: unknown): value is Record<string, unknown> {
	return !!value && typeof value === 'object' && !Array.isArray(value);
}

function deepMerge<T>(base: T, overrides: unknown): T {
	if (!isPlainObject(base) || !isPlainObject(overrides)) {
		return (overrides ?? base) as T;
	}

	const result: Record<string, unknown> = { ...(base as Record<string, unknown>) };

	for (const [key, overrideValue] of Object.entries(overrides)) {
		const baseValue = result[key];
		if (isPlainObject(baseValue) && isPlainObject(overrideValue)) {
			result[key] = deepMerge(baseValue, overrideValue);
			continue;
		}
		result[key] = overrideValue;
	}

	return result as T;
}

@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
	private readonly promiseMap: Record<string, Promise<Translation>> = {};
	private readonly valueMap: Record<string, Translation> = {};
	private readonly isDefaultClient = environment.clientId === 'default';
	private i18nOverridePathsPromise: Promise<Set<string>> | null = null;

	/** Paths listed in assets/i18n/override/manifest.json for which delta JSON should be fetched. */
	private getI18nOverridePaths(): Promise<Set<string>> {
		if (this.isDefaultClient) {
			return Promise.resolve(new Set());
		}

		if (!this.i18nOverridePathsPromise) {
			this.i18nOverridePathsPromise = (async () => {
				const manifestUrl = new URL('assets/i18n/override/manifest.json', document.baseURI);
				manifestUrl.searchParams.set('t', BUILD_TIME);

				try {
					const resp = await fetch(manifestUrl.toString());
					if (!resp.ok) {
						return new Set<string>();
					}
					const data = (await resp.json()) as { paths?: unknown };
					const paths = Array.isArray(data.paths) ? data.paths : [];
					return new Set(paths.filter((p): p is string => typeof p === 'string'));
				} catch {
					return new Set<string>();
				}
			})();
		}

		return this.i18nOverridePathsPromise;
	}

	public async getTranslation(path: string): Promise<Translation> {
		const oldPathArr = path.split('/');
		const pathRaw = oldPathArr.slice(0, -1);
		// We check if it's the first time the app loads to load the general translation file or the specific one for the selected page.
		const objectPath = pathRaw.length === 0 ? ['general'] : pathRaw;
		const newPath = [...oldPathArr.slice(-1), ...objectPath].join('/');

		const storedValue = this.valueMap[newPath];
		if (storedValue) {
			// console.debug(`[i18n] ${newPath} (cached)`);
			return storedValue;
		}

		const storedPromise = this.promiseMap[newPath];
		if (storedPromise) {
			// console.debug(`[i18n] ${newPath} (repeated)`);
			const i18n = await storedPromise;
			return i18n;
		}

		// console.debug(`[i18n] ${path} -> ${newPath}`);

		// console.debug(`[i18n] ${newPath} (fetching)`);

		if (newPath === 'undefined/general') {
			return {};
		}

		const baseUrl = new URL(`assets/i18n/${newPath}.json`, document.baseURI);
		baseUrl.searchParams.set('t', BUILD_TIME);

		const overrideUrl = new URL(`assets/i18n/override/${newPath}.json`, document.baseURI);
		overrideUrl.searchParams.set('t', BUILD_TIME);

		this.promiseMap[newPath] = new Promise<Translation>(async (resolve) => {
			try {
				const [baseResp, overridePaths] = await Promise.all([
					fetch(baseUrl.toString()),
					this.getI18nOverridePaths(),
				]);

				const base = baseResp.ok ? ((await baseResp.json()) as Translation) : {};
				let overrides: Translation = {};
				if (overridePaths.size > 0 && overridePaths.has(newPath)) {
					const overrideResp = await fetch(overrideUrl.toString());
					overrides = overrideResp.ok ? ((await overrideResp.json()) as Translation) : {};
				}
				const i18n = deepMerge(base, overrides);

				this.valueMap[newPath] = i18n;
				delete this.promiseMap[newPath];
				resolve(i18n);
			} catch (error) {
				console.error(`[i18n] (${newPath})`, error);
				resolve({});
				// reject(error);
			}
		});

		// console.debug(`[i18n] ${newPath} (finished)`);

		const i18n = await this.promiseMap[newPath];
		return i18n;
	}
}

@NgModule({
	exports: [TranslocoModule],
	providers: [
		provideTransloco({
			config: {
				availableLangs: Object.values(ReachTranslateLang),
				defaultLang: ReachTranslateLang.ENGLISH_GB,
				fallbackLang: [ReachTranslateLang.ENGLISH_GB, ReachTranslateLang.UNIVERSAL],
				missingHandler: {
					logMissingKey: true,
					useFallbackTranslation: true,
					allowEmpty: true,
				},
				reRenderOnLangChange: true,
				prodMode: environment.production,
			},
			loader: TranslocoHttpLoader,
		}),
	],
})
export class TranslocoRootModule {}
